import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Star, Heart, Share2, Bell, ThumbsUp, ThumbsDown, MessageCircle, ArrowDown, ArrowUp,Award, TrendingUp, Flame, UserPlus, Lock,Zap, Eye, MapPin, Shield, Plus, X, Send, ChevronDown, ChevronUp } from 'lucide-react';
import PriceTracker from '../components/PriceTrackChart';
import { ApiService } from '../services/productService';
import API_URL from '../config/ApiUrl';
import { getProductImage } from '../static/ProductPlaceHolder';
import AuthGate from '../auth/AuthGate';
import { useUser } from '../context/UserContext';
import { useTheme } from '../arrangement/ThemeContext';
import { getSmokerEmoji } from '../static/SmokerEmoji';
import { ProductDispensariesModal } from '../dispensary/ProductDispensaryModal';
// Review Form Component
const ReviewStatsCard = ({ stats, reviewStatsSmoker, colors }) => {

  if (!stats || stats.total_reviews === 0) return null;

  // Process smoker level statistics from reviewStatsSmoker
  const smokerLevels = ['beginner', 'casual', 'connoisseur', 'stoner'];
  const smokerStats = smokerLevels.reduce((acc, level) => {
    const reviews = reviewStatsSmoker?.filter(
      (review) => review.smoker_level.toLowerCase() === level
    ) || [];
    
    const count = reviews.length;
    const averageRating = count > 0 
      ? (reviews.reduce((sum, review) => sum + review.rating, 0) / count).toFixed(1)
      : '0.0';

    acc[level] = {
      count,
      averageRating,
    };
    return acc;
  }, {});

  // Define smoker level display data
  const smokerLevelData = [
    {
      level: 'Beginner',
      image: 'https://highmind-bucket.s3.us-east-1.amazonaws.com/cdb53b7a8c7e4624a810c0d654ca03c8_Smoker_beginer.webp',
      color: '#8b5cf6',
      stats: smokerStats.beginner,
    },
    {
      level: 'Casual',
      image: 'https://highmind-bucket.s3.us-east-1.amazonaws.com/e72017f33d1c480db1928ca3d7295de4_Smoker_Level_cas.webp',
      color: '#ef4444',
      stats: smokerStats.casual,
    },
    {
      level: 'Connoisseur',
      image: 'https://highmind-bucket.s3.us-east-1.amazonaws.com/3e1e13711dd441fcb7c34c155416ee69_Smoker_Level_con.webp',
      color: '#00d4aa',
      stats: smokerStats.connoisseur,
    },
    {
      level: 'Stoner',
      image: 'https://highmind-bucket.s3.us-east-1.amazonaws.com/9084554e27d747188148530bc7899eee_Smoker_Level_Logo_Stoner.webp',
      color: '#4480ef',
      stats: smokerStats.stoner,
    },
  ];

  return (
    <div style={{ background: colors.bgTertiary, border: '1px solid #333', borderRadius: '12px', padding: '0px 16px 16px 16px', marginBottom: '16px' }}>
      <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#e5e5e5', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
        🌿 Smoker Level Reviews
      </h4>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', fontSize: '12px' }}>
        {smokerLevelData.map(({ level, image, color, stats }) => (
          <div key={level} style={{ textAlign: 'center' }}>
            <img
              src={image}
              alt={level}
              style={{
                width: window.innerWidth > 640 ? '4.125rem' : '0.875rem',
                height: window.innerWidth > 640 ? '4.125rem' : '0.875rem',
              }}
            />
            <div style={{ color: '#888' }}>{level}</div>
            <div style={{ color, fontSize: '14px' }}>
              ⭐ {stats.averageRating}/5 ({stats.count})
            </div>
          </div>
        ))}
      </div>
      <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#e5e5e5', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px', marginTop: '16px' }}>
        📊 Review Quality Statistics
      </h4>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', fontSize: '12px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#00d4aa', fontWeight: '600', fontSize: '16px' }}>
            {stats.valid_reviews.count}
          </div>
          <div style={{ color: '#888' }}>Quality Reviews</div>
          <div style={{ color: '#00d4aa', fontSize: '10px' }}>
            ⭐ {stats.valid_reviews.average_rating}/5
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#8b5cf6', fontWeight: '600', fontSize: '16px' }}>
            {stats.discovery_reviews.count}
          </div>
          <div style={{ color: '#888' }}>Discovery Swipes</div>
          <div style={{ color: '#8b5cf6', fontSize: '10px' }}>
            ⭐ {stats.discovery_reviews.average_rating}/5
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#ef4444', fontWeight: '600', fontSize: '16px' }}>
            {stats.no_text_reviews.count}
          </div>
          <div style={{ color: '#888' }}>No Content</div>
          <div style={{ color: '#ef4444', fontSize: '10px' }}>
            ⭐ {stats.no_text_reviews.average_rating}/5
          </div>
        </div>
      </div>
      {stats.filtered_out_count > 0 && (
        <div style={{ marginTop: '12px', padding: '8px', background: colors.bgCard, borderRadius: '6px', textAlign: 'center', fontSize: '11px', color: '#888' }}>
          Filtered out {stats.filtered_out_count} low-quality reviews ({stats.filter_percentage}% of total)
        </div>
      )}
    </div>
  );
};

const ReviewForm = ({ onSubmit, onClose, productId, colors }) => {
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [effects, setEffects] = useState([]);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const effectOptions = ["Euphoric", "Creative", "Relaxing", "Energizing", "Happy", "Sleepy", "Focused", "Uplifting"];

  const handleEffectToggle = (effect) => {
    setEffects(prev => 
      prev.includes(effect) 
        ? prev.filter(e => e !== effect)
        : [...prev, effect]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const reviewData = {
        product_id: productId,
        rating: rating,
        content: content,
        effects: effects
      };
      
      await onSubmit(reviewData);
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '16px' : '20px'
    }}>
      <div style={{
        background: colors.bgTertiary,
        borderRadius: '20px',
        padding: isMobile ? '24px 20px' : '32px',
        width: '100%',
        maxWidth: '600px',
        maxHeight: isMobile ? '90vh' : '80vh',
        overflow: 'auto',
        border: '1px solid #333'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: isMobile ? '20px' : '24px',
            fontWeight: '700',
            color: '#e5e5e5',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Star size={isMobile ? 20 : 24} style={{ color: '#00d4aa' }} />
            Write a Review
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#888',
              cursor: 'pointer',
              padding: '8px'
            }}
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Star Rating */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: '600',
              color: '#e5e5e5',
              marginBottom: '12px'
            }}>
              Your Rating
            </label>
            <div style={{ display: 'flex', gap: '4px', marginBottom: '8px', justifyContent: isMobile ? 'center' : 'flex-start' }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredStar(star)}
                  onMouseLeave={() => setHoveredStar(0)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px'
                  }}
                >
                  <Star
                    size={isMobile ? 20 : 24}
                    style={{
                      color: (hoveredStar || rating) >= star ? '#fbbf24' : '#333',
                      fill: (hoveredStar || rating) >= star ? '#fbbf24' : 'none',
                      transition: 'all 0.2s ease'
                    }}
                  />
                </button>
              ))}
            </div>
            <p style={{ fontSize: '12px', color: '#888', margin: 0, textAlign: isMobile ? 'center' : 'left' }}>
              {rating > 0 && `${rating}/5 - ${rating >= 4 ? 'Excellent' : rating >= 3 ? 'Good' : rating >= 2 ? 'Average' : 'Poor'}`}
            </p>
          </div>

          {/* Review Content */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: '600',
              color: '#e5e5e5',
              marginBottom: '8px'
            }}>
              Your Review
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Share your detailed experience with this product..."
              rows={isMobile ? 3 : 4}
              style={{
                width: '100%',
                padding: isMobile ? '10px 12px' : '12px 16px',
                backgroundColor: '#2a2a2a',
                border: '1px solid #333',
                borderRadius: '8px',
                color: '#e5e5e5',
                fontSize: isMobile ? '13px' : '14px',
                resize: 'vertical',
                boxSizing: 'border-box',
                outline: 'none',
                fontFamily: 'inherit'
              }}
            />
          </div>

          {/* Effects Selection */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: '600',
              color: '#e5e5e5',
              marginBottom: '12px'
            }}>
              Effects You Experienced
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {effectOptions.map((effect) => (
                <button
                  key={effect}
                  type="button"
                  onClick={() => handleEffectToggle(effect)}
                  style={{
                    padding: isMobile ? '5px 10px' : '6px 12px',
                    background: effects.includes(effect) 
                      ? 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)' 
                      : '#2a2a2a',
                    color: effects.includes(effect) ? '#0a0a0a' : '#e5e5e5',
                    border: '1px solid',
                    borderColor: effects.includes(effect) ? '#00d4aa' : '#333',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: isMobile ? '11px' : '12px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {effect}
                </button>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'flex-end',
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: isMobile ? '10px 20px' : '12px 24px',
                background: colors.bgCard,
                color: '#e5e5e5',
                border: '1px solid #333',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: isMobile ? '13px' : '14px',
                fontWeight: '500',
                order: isMobile ? 2 : 1
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!rating || rating < 1 || rating > 5 || !content.trim() || isSubmitting}
              style={{
                padding: isMobile ? '10px 20px' : '12px 24px',
                background: (rating && content.trim() && !isSubmitting) 
                  ? 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)' 
                  : '#333',
                color: (rating && content.trim() && !isSubmitting) ? '#0a0a0a' : '#666',
                border: 'none',
                borderRadius: '8px',
                cursor: (rating && content.trim() && !isSubmitting) ? 'pointer' : 'not-allowed',
                fontSize: isMobile ? '13px' : '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                order: isMobile ? 1 : 2
              }}
            >
              {isSubmitting ? (
                <>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #666',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Submitting...
                </>
              ) : (
                <>
                  <Send size={16} />
                  Submit Review
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ReviewCard = ({ review, onUpvote, onDownvote, onReplySubmit, colors }) => {
  const [showReplies, setShowReplies] = useState(false);
  const [replies, setReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleVote = async (voteValue) => {
    try {
      await onUpvote(review.id, voteValue);
    } catch (error) {
      alert('Failed to vote. Please try again.');
    }
  };

  const handleVoteReply = async (replyId, voteValue) => {
    try {
      const result = await ApiService.voteReviewReply(replyId, voteValue);
      
      // Update the reply vote in local state
      setReplies(prev => prev.map(reply => 
        reply.id === replyId 
          ? { 
              ...reply, 
              vote_score: result.total_votes,
              user_vote: result.user_vote 
            }
          : reply
      ));
    } catch (error) {
      console.error('Error voting on reply:', error);
      throw error;
    }
  };
  
  const handleNestedReplySubmit = (replyId) => {
    // Update nested reply count for the specific reply
    setReplies(prev => prev.map(reply =>
      reply.id === replyId
        ? { ...reply, nested_reply_count: (reply.nested_reply_count || 0) + 1 }
        : reply
    ));
  };

  const loadReplies = async () => {
    if (replies.length > 0) return; // Already loaded
    
    setLoadingReplies(true);
    try {
      const repliesData = await ApiService.getReviewReplies(review.id);
      setReplies(repliesData);
    } catch (error) {
      console.error('Error loading replies:', error);
    } finally {
      setLoadingReplies(false);
    }
  };

  const handleShowReplies = () => {
    setShowReplies(!showReplies);
    if (!showReplies) {
      loadReplies();
    }
  };

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    if (!replyContent.trim()) return;

    setIsSubmittingReply(true);
    try {
      const newReply = await ApiService.createReviewReply(review.id, replyContent);
      setReplies(prev => [...prev, newReply]);
      setReplyContent('');
      setShowReplyForm(false);
      
      // Update parent review reply count
      if (onReplySubmit) {
        onReplySubmit(review.id);
      }
    } catch (error) {
      alert('Failed to submit reply. Please try again.');
    } finally {
      setIsSubmittingReply(false);
    }
  };

  return (
    <div style={{
      background: colors.bgTertiary,
      border: '1px solid #333',
      borderRadius: '16px',
      padding: isMobile ? '16px' : '20px',
      marginBottom: '16px',
      transition: 'all 0.3s ease',
      display: 'flex',
      gap: isMobile ? '12px' : '20px',
      flexDirection: isMobile ? 'column' : 'row'
    }}>
      
      {/* Voting System - Mobile: Top, Desktop: Left */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'row' : 'column',
        alignItems: 'center',
        justifyContent: isMobile ? 'center' : 'flex-start',
        gap: isMobile ? '16px' : '8px',
        minWidth: isMobile ? 'auto' : '70px',
        width: isMobile ? '90%' : 'auto',
        background: 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%)',
        padding: isMobile ? '12px 16px' : '16px 12px',
        borderRadius: '16px',
        border: '1px solid #333',
        order: isMobile ? 2 : 1,
        height: 'fit-content'
      }}>
        <button
          onClick={() => handleVote(review.user_vote === 1 ? 0 : 1)}
          style={{
            padding: isMobile ? '6px' : '8px',
            borderRadius: '12px',
            border: 'none',
            background: review.user_vote === 1
              ? 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)'
              : 'white',
            color: review.user_vote === 1 ? 'white' : '#6b7280',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: review.user_vote === 1 ? 'scale(1.1)' : 'scale(1)',
            boxShadow: review.user_vote === 1 ? '0 4px 12px rgba(16, 185, 129, 0.4)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            if (review.user_vote !== 1) {
              e.currentTarget.style.background = 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)';
              e.currentTarget.style.color = '#00d4aa';
              e.currentTarget.style.transform = 'scale(1.05)';
            }
          }}
          onMouseLeave={(e) => {
            if (review.user_vote !== 1) {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.color = '#6b7280';
              e.currentTarget.style.transform = 'scale(1)';
            }
          }}
        >
          <ArrowUp size={isMobile ? 16 : 20} />
        </button>
        
        <span style={{
          fontWeight: 'bold',
          fontSize: isMobile ? '16px' : '20px',
          color: review.user_vote === 1 ? '#00d4aa' :
                 review.user_vote === -1 ? '#ef4444' : '#374151',
          transition: 'all 0.3s ease'
        }}>
          {review.likes || 0}
        </span>
        
        <button
          onClick={() => handleVote(review.user_vote === -1 ? 0 : -1)}
          style={{
            padding: isMobile ? '6px' : '8px',
            borderRadius: '12px',
            border: 'none',
            background: review.user_vote === -1
              ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
              : 'white',
            color: review.user_vote === -1 ? 'white' : '#6b7280',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: review.user_vote === -1 ? 'scale(1.1)' : 'scale(1)',
            boxShadow: review.user_vote === -1 ? '0 4px 12px rgba(239, 68, 68, 0.4)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            if (review.user_vote !== -1) {
              e.currentTarget.style.background = 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)';
              e.currentTarget.style.color = '#ef4444';
              e.currentTarget.style.transform = 'scale(1.05)';
            }
          }}
          onMouseLeave={(e) => {
            if (review.user_vote !== -1) {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.color = '#6b7280';
              e.currentTarget.style.transform = 'scale(1)';
            }
          }}
        >
          <ArrowDown size={isMobile ? 16 : 20} />
        </button>
      </div>

      {/* Review Content - Right Side */}
      <div style={{ flex: 1, order: isMobile ? 1 : 2 }}>
        {/* Review Header */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          justifyContent: 'space-between', 
          marginBottom: '12px',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '8px' : '0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px' }}>
            <img 
            src={review.cover_image ? `https://highmind-bucket.s3.us-east-1.amazonaws.com/${review.cover_image}` : review.avatar}
            style={{
              width: isMobile ? '32px' : '40px',
              height: isMobile ? '32px' : '40px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: '600',
              color: '#0a0a0a'
            }}>
            </img>
            <img 
              src={getSmokerEmoji(review.smoker_level)}
              alt={`${review.smoker_level} level`}
              style={{
                width: isMobile ? '36px' : '44px',
                height: isMobile ? '36px' : '44px',
                borderRadius: '50%',
                objectFit: 'cover'
              }}
            />
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ 
                  fontWeight: '600', 
                  color: '#e5e5e5',
                  fontSize: isMobile ? '14px' : '16px'
                }}>
                  {review.display_name ||review.username || 'Anonymous'}
                </span>
                {review.verified && (
                  <div style={{
                    background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                    color: '#0a0a0a',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px'
                  }}>
                    <Shield size={8} />
                    VERIFIED
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Star Rating */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <div style={{ display: 'flex' }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  size={isMobile ? 10 : 12}
                  style={{
                    color: star <= review.rating ? '#fbbf24' : '#333',
                    fill: star <= review.rating ? '#fbbf24' : 'none'
                  }}
                />
              ))}
            </div>
            <span style={{ 
              fontWeight: '600', 
              color: '#e5e5e5', 
              marginLeft: '4px',
              fontSize: isMobile ? '12px' : '14px'
            }}>
              {review.rating}/5
            </span>
          </div>
        </div>

        {/* Review Title */}
        <h4 style={{
          fontSize: isMobile ? '14px' : '16px',
          fontWeight: '600',
          color: '#e5e5e5',
          marginBottom: '8px',
          margin: '0 0 8px 0'
        }}>
          {review.title}
        </h4>

        {/* Review Content */}
        <p style={{
          color: '#b3b3b3',
          lineHeight: '1.6',
          marginBottom: '12px',
          margin: '0 0 12px 0',
          fontSize: isMobile ? '13px' : '14px'
        }}>
          {review.content}
        </p>

        {/* Effects Tags */}
        {review.effects && review.effects.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
            {review.effects.map((effect, index) => (
              <span
                key={effect}
                style={{
                  fontSize: isMobile ? '10px' : '11px',
                  padding: '4px 8px',
                  backgroundColor: index % 2 === 0 ? 'rgba(0, 212, 170, 0.2)' : 'rgba(139, 92, 246, 0.2)',
                  color: index % 2 === 0 ? '#00d4aa' : '#8b5cf6',
                  borderRadius: '6px',
                  fontWeight: '500'
                }}
              >
                {effect}
              </span>
            ))}
          </div>
        )}

        {/* Review Actions */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: '12px',
          borderTop: '1px solid #333',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '8px' : '0'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            width: isMobile ? '100%' : 'auto',
            justifyContent: isMobile ? 'space-around' : 'flex-start'
          }}>
            <button 
              onClick={handleShowReplies}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'transparent',
                border: 'none',
                color: '#888',
                cursor: 'pointer',
                fontSize: isMobile ? '12px' : '14px',
                padding: '4px 8px',
                borderRadius: '6px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <MessageCircle size={isMobile ? 12 : 14} />
              {review.reply_count > 0 ? `${review.reply_count} Replies` : 'Reply'}
              {showReplies ? <ChevronUp size={isMobile ? 12 : 14} /> : <ChevronDown size={isMobile ? 12 : 14} />}
            </button>

            <button 
              onClick={() => setShowReplyForm(!showReplyForm)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'transparent',
                border: 'none',
                color: '#00d4aa',
                cursor: 'pointer',
                fontSize: isMobile ? '12px' : '14px',
                padding: '4px 8px',
                borderRadius: '6px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <Plus size={isMobile ? 12 : 14} />
              Add Reply
            </button>
          </div>
        </div>

        {/* Reply Form */}
        {showReplyForm && (
          <form onSubmit={handleReplySubmit} style={{
            marginTop: '16px',
            padding: isMobile ? '12px' : '16px',
            background: colors.bgCard,
            borderRadius: '12px',
            border: '1px solid #333'
          }}>
            <textarea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder="Write your reply..."
              rows={isMobile ? 2 : 3}
              style={{
                width: '100%',
                padding: isMobile ? '8px' : '12px',
                backgroundColor: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: '8px',
                color: '#e5e5e5',
                fontSize: isMobile ? '13px' : '14px',
                resize: 'vertical',
                marginBottom: '12px',
                boxSizing: 'border-box',
                outline: 'none',
                fontFamily: 'inherit'
              }}
            />
            <div style={{ 
              display: 'flex', 
              gap: '8px', 
              justifyContent: 'flex-end',
              flexDirection: isMobile ? 'column' : 'row'
            }}>
              <button
                type="button"
                onClick={() => setShowReplyForm(false)}
                style={{
                  padding: isMobile ? '8px 16px' : '8px 16px',
                  background: 'transparent',
                  color: '#888',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  order: isMobile ? 2 : 1
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!replyContent.trim() || isSubmittingReply}
                style={{
                  padding: isMobile ? '8px 16px' : '8px 16px',
                  background: replyContent.trim() && !isSubmittingReply
                    ? 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)'
                    : '#333',
                  color: replyContent.trim() && !isSubmittingReply ? '#0a0a0a' : '#666',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: replyContent.trim() && !isSubmittingReply ? 'pointer' : 'not-allowed',
                  fontSize: '12px',
                  fontWeight: '600',
                  order: isMobile ? 1 : 2
                }}
              >
                {isSubmittingReply ? 'Posting...' : 'Post Reply'}
              </button>
            </div>
          </form>
        )}

        {/* Replies Thread - Include all existing reply components here */}
        {showReplies && (
          <div style={{
            marginTop: '16px',
            paddingLeft: isMobile ? '8px' : '16px',
            borderLeft: '2px solid #333'
          }}>
            {loadingReplies ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid #333',
                  borderTop: '2px solid #00d4aa',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 8px'
                }} />
                Loading replies...
              </div>
            ) : replies.length > 0 ? (
              replies.map((reply) => (
                <ReplyCard 
                  key={reply.id} 
                  reply={reply} 
                  onVoteReply={handleVoteReply}
                  onNestedReplySubmit={handleNestedReplySubmit}
                  colors={colors}
                />
              ))
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
                No replies yet. Be the first to respond!
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const NestedReplyCard = ({ nestedReply, onVoteNestedReply, onNestedReplySubmit, parentReplyId, depth = 0, colors }) => {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [showSubReplies, setShowSubReplies] = useState(false);
  const [subReplies, setSubReplies] = useState(nestedReply.nested_replies || []);
  const [loadingSubReplies, setLoadingSubReplies] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleVote = async (voteValue) => {
    try {
      await onVoteNestedReply(nestedReply.id, voteValue);
    } catch (error) {
      alert('Failed to vote on reply. Please try again.');
    }
  };

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    if (!replyContent.trim()) return;

    setIsSubmittingReply(true);
    try {
      const newSubReply = await ApiService.createReviewReply(
        nestedReply.review_id, 
        replyContent, 
        nestedReply.id
      );
      
      setSubReplies(prev => [...prev, newSubReply]);
      setReplyContent('');
      setShowReplyForm(false);
      setShowSubReplies(true);
      
      if (onNestedReplySubmit) {
        onNestedReplySubmit(nestedReply.id);
      }
    } catch (error) {
      alert('Failed to submit reply. Please try again.');
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const handleVoteSubReply = async (subReplyId, voteValue) => {
    try {
      const result = await ApiService.voteReviewReply(subReplyId, voteValue);
      
      setSubReplies(prev => prev.map(subReply => 
        subReply.id === subReplyId 
          ? { 
              ...subReply, 
              vote_score: result.total_votes,
              user_vote: result.user_vote 
            }
          : subReply
      ));
    } catch (error) {
      console.error('Error voting on sub reply:', error);
      throw error;
    }
  };

  const handleSubReplySubmit = async (subReplyId) => {
    try {
      const updatedReplies = await ApiService.getReviewReplies(nestedReply.review_id);
      
      const findNestedReply = (replies) => {
        for (const reply of replies) {
          if (reply.id === nestedReply.id) {
            return reply.sub_replies || reply.nested_replies || [];
          }
          if (reply.nested_replies) {
            const found = findNestedReply(reply.nested_replies);
            if (found.length > 0) return found;
          }
          if (reply.sub_replies) {
            const found = findNestedReply(reply.sub_replies);
            if (found.length > 0) return found;
          }
        }
        return [];
      };

      const updatedSubReplies = findNestedReply(updatedReplies);
      setSubReplies(updatedSubReplies);
      
      if (onNestedReplySubmit) {
        onNestedReplySubmit(nestedReply.id);
      }
    } catch (error) {
      console.error('Error refreshing sub replies:', error);
    }
  };

  const loadSubReplies = async () => {
    if (subReplies.length > 0 || !nestedReply.sub_reply_count) return;
    
    setLoadingSubReplies(true);
    try {
      const subRepliesData = await ApiService.getReplySubReplies(nestedReply.id);
      setSubReplies(subRepliesData);
    } catch (error) {
      console.error('Error loading sub replies:', error);
    } finally {
      setLoadingSubReplies(false);
    }
  };

  const handleShowSubReplies = () => {
    setShowSubReplies(!showSubReplies);
    if (!showSubReplies) {
      loadSubReplies();
    }
  };

  const maxDepth = 5;
  const currentDepth = Math.min(depth, maxDepth);
  const indentSize = Math.max(8, 16 - (currentDepth * 2));
  const fontSize = Math.max(10, 12 - (currentDepth * 0.5));

  return (
    <div style={{
      background: depth % 2 === 0 ? '#1a1a1a' : '#242424',
      border: '1px solid #333',
      borderRadius: '8px',
      padding: isMobile ? '8px' : '12px',
      marginBottom: '8px',
      marginLeft: isMobile ? `${Math.min(currentDepth * 8, 24)}px` : `${currentDepth * indentSize}px`
    }}>
      <div style={{ display: 'flex', gap: isMobile ? '6px' : '8px' }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '2px',
          minWidth: isMobile ? '20px' : '24px'
        }}>
          <button
            onClick={() => handleVote(nestedReply.user_vote === 1 ? 0 : 1)}
            style={{
              padding: '2px',
              border: 'none',
              background: nestedReply.user_vote === 1 ? '#00d4aa' : 'transparent',
              color: nestedReply.user_vote === 1 ? 'white' : '#888',
              borderRadius: '3px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <ArrowUp size={Math.max(8, 10 - currentDepth)} />
          </button>
          
          <span style={{
            fontSize: `${Math.max(8, 10 - currentDepth)}px`,
            fontWeight: '600',
            color: (nestedReply.vote_score || 0) > 0 ? '#00d4aa' : 
                   (nestedReply.vote_score || 0) < 0 ? '#ef4444' : '#888'
          }}>
            {nestedReply.vote_score || 0}
          </span>
          
          <button
            onClick={() => handleVote(nestedReply.user_vote === -1 ? 0 : -1)}
            style={{
              padding: '2px',
              border: 'none',
              background: nestedReply.user_vote === -1 ? '#ef4444' : 'transparent',
              color: nestedReply.user_vote === -1 ? 'white' : '#888',
              borderRadius: '3px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <ArrowDown size={Math.max(8, 10 - currentDepth)} />
          </button>
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
            <img 
              src={nestedReply.cover_image_url || nestedReply.profile_image_url}
              style={{
                width: `${Math.max(16, 20 - currentDepth)}px`,
                height: `${Math.max(16, 20 - currentDepth)}px`,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${depth % 3 === 0 ? '#f59e0b' : depth % 3 === 1 ? '#8b5cf6' : '#00d4aa'} 0%, ${depth % 3 === 0 ? '#d97706' : depth % 3 === 1 ? '#7c3aed' : '#10b981'} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: `${Math.max(8, 10 - currentDepth)}px`,
                fontWeight: '600',
                color: 'white'
              }}
            />
            <img 
              src={getSmokerEmoji(nestedReply.smoker_level)}
              alt={`${nestedReply.smoker_level} level`}
              style={{
                width: isMobile ? '24px' : '30px',
                height: isMobile ? '24px' : '30px',
                borderRadius: '50%',
                objectFit: 'cover'
              }}
            />
            <span style={{ fontSize: `${fontSize}px`, fontWeight: '600', color: '#e5e5e5' }}>
              {nestedReply.display_name || nestedReply.username}
            </span>
            <span style={{ fontSize: `${Math.max(8, fontSize - 2)}px`, color: '#666' }}>
              {new Date(nestedReply.created_at).toLocaleDateString()}
            </span>
          </div>
          
          <p style={{ 
            color: '#b3b3b3', 
            fontSize: `${fontSize}px`, 
            margin: '0 0 6px 0',
            lineHeight: '1.4'
          }}>
            {nestedReply.content}
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: `${Math.max(8, fontSize - 1)}px` }}>
            <button 
              onClick={() => setShowReplyForm(!showReplyForm)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#888',
                cursor: 'pointer',
                fontSize: `${Math.max(8, fontSize - 1)}px`,
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
                padding: '2px 4px',
                borderRadius: '3px',
                transition: 'all 0.2s ease'
              }}
            >
              <MessageCircle size={Math.max(8, 10 - currentDepth)} />
              Reply
            </button>

            {(subReplies.length > 0 || nestedReply.nested_replies?.length > 0) && (
              <button
                onClick={handleShowSubReplies}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#00d4aa',
                  cursor: 'pointer',
                  fontSize: `${Math.max(8, fontSize - 1)}px`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '3px',
                  padding: '2px 4px',
                  borderRadius: '3px'
                }}
              >
                {subReplies.length || nestedReply.nested_replies?.length || 0} replies
                {showSubReplies ? <ChevronUp size={Math.max(8, 10 - currentDepth)} /> : <ChevronDown size={Math.max(8, 10 - currentDepth)} />}
              </button>
            )}
          </div>

          {showReplyForm && (
            <form onSubmit={handleReplySubmit} style={{
              marginTop: '8px',
              padding: isMobile ? '6px' : '8px',
              background: colors.bgCard,
              borderRadius: '6px',
              border: '1px solid #444'
            }}>
              <textarea
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder={`Reply to ${nestedReply.username}...`}
                rows={isMobile ? 2 : 2}
                style={{
                  width: '100%',
                  padding: '6px',
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #333',
                  borderRadius: '4px',
                  color: '#e5e5e5',
                  fontSize: `${fontSize}px`,
                  resize: 'vertical',
                  marginBottom: '6px',
                  boxSizing: 'border-box',
                  outline: 'none',
                  fontFamily: 'inherit'
                }}
              />
              <div style={{ 
                display: 'flex', 
                gap: '4px', 
                justifyContent: 'flex-end',
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                <button
                  type="button"
                  onClick={() => setShowReplyForm(false)}
                  style={{
                    padding: '4px 8px',
                    background: 'transparent',
                    color: '#888',
                    border: '1px solid #333',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    fontSize: `${Math.max(8, fontSize - 1)}px`,
                    order: isMobile ? 2 : 1
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!replyContent.trim() || isSubmittingReply}
                  style={{
                    padding: '4px 8px',
                    background: replyContent.trim() && !isSubmittingReply
                      ? 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)'
                      : '#333',
                    color: replyContent.trim() && !isSubmittingReply ? '#0a0a0a' : '#666',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: replyContent.trim() && !isSubmittingReply ? 'pointer' : 'not-allowed',
                    fontSize: `${Math.max(8, fontSize - 1)}px`,
                    fontWeight: '600',
                    order: isMobile ? 1 : 2
                  }}
                >
                  {isSubmittingReply ? 'Posting...' : 'Reply'}
                </button>
              </div>
            </form>
          )}

          {showSubReplies && (
            <div style={{
              marginTop: '8px',
              paddingLeft: isMobile ? '4px' : '8px',
              borderLeft: '1px solid #444'
            }}>
              {loadingSubReplies ? (
                <div style={{ padding: '12px', textAlign: 'center', color: '#888' }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    border: '2px solid #333',
                    borderTop: '2px solid #00d4aa',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 4px'
                  }} />
                  Loading...
                </div>
              ) : subReplies.length > 0 ? (
                subReplies.map((subReply) => (
                  <NestedReplyCard 
                    key={subReply.id} 
                    nestedReply={subReply}
                    onVoteNestedReply={handleVoteSubReply}
                    onNestedReplySubmit={handleSubReplySubmit}
                    parentReplyId={nestedReply.id}
                    depth={depth + 1}
                    colors={colors}
                  />
                ))
              ) : (
                <div style={{ 
                  padding: '12px', 
                  textAlign: 'center', 
                  color: '#666',
                  fontSize: `${Math.max(8, fontSize - 1)}px`
                }}>
                  No replies yet
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ReplyCard = ({ reply, onVoteReply, onNestedReplySubmit, colors }) => {
  const [showNestedReplies, setShowNestedReplies] = useState(false);
  const [showNestedReplyForm, setShowNestedReplyForm] = useState(false);
  const [nestedReplyContent, setNestedReplyContent] = useState('');
  const [isSubmittingNestedReply, setIsSubmittingNestedReply] = useState(false);
  const [nestedReplies, setNestedReplies] = useState(reply.nested_replies || []);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleVoteReply = async (voteValue) => {
    try {
      await onVoteReply(reply.id, voteValue);
    } catch (error) {
      alert('Failed to vote on reply. Please try again.');
    }
  };

  const handleNestedReplySubmit = async (e) => {
    e.preventDefault();
    if (!nestedReplyContent.trim()) return;

    setIsSubmittingNestedReply(true);
    try {
      const newNestedReply = await ApiService.createReviewReply(
        reply.review_id, 
        nestedReplyContent, 
        reply.id
      );
      
      setNestedReplies(prev => [...prev, newNestedReply]);
      setNestedReplyContent('');
      setShowNestedReplyForm(false);
      
      if (onNestedReplySubmit) {
        onNestedReplySubmit(reply.id);
      }
    } catch (error) {
      alert('Failed to submit nested reply. Please try again.');
    } finally {
      setIsSubmittingNestedReply(false);
    }
  };

  const handleVoteNestedReply = async (nestedReplyId, voteValue) => {
    try {
      const result = await ApiService.voteReviewReply(nestedReplyId, voteValue);
      
      const updateNestedReplyVote = (replies) => {
        return replies.map(nestedReply => {
          if (nestedReply.id === nestedReplyId) {
            return {
              ...nestedReply,
              vote_score: result.total_votes,
              user_vote: result.user_vote
            };
          }
          if (nestedReply.sub_replies) {
            return {
              ...nestedReply,
              sub_replies: updateNestedReplyVote(nestedReply.sub_replies)
            };
          }
          return nestedReply;
        });
      };

      setNestedReplies(prev => updateNestedReplyVote(prev));
    } catch (error) {
      console.error('Error voting on nested reply:', error);
      throw error;
    }
  };

  const handleNestedReplyToNestedReply = async (nestedReplyId) => {
    try {
      const updatedReplies = await ApiService.getReviewReplies(reply.review_id);
      const updatedReply = updatedReplies.find(r => r.id === reply.id);
      if (updatedReply && updatedReply.nested_replies) {
        setNestedReplies(updatedReply.nested_replies);
      }
      
      if (onNestedReplySubmit) {
        onNestedReplySubmit(reply.id);
      }
    } catch (error) {
      console.error('Error refreshing nested replies:', error);
    }
  };

  return (
    <div style={{
      background: colors.bgCard,
      border: '1px solid #333',
      borderRadius: '12px',
      padding: isMobile ? '12px' : '16px',
      marginBottom: '12px'
    }}>
      <div style={{ display: 'flex', gap: isMobile ? '8px' : '12px' }}>
        
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '4px'
        }}>
          <button
            onClick={() => handleVoteReply(reply.user_vote === 1 ? 0 : 1)}
            style={{
              padding: '4px',
              border: 'none',
              background: reply.user_vote === 1 ? '#00d4aa' : 'transparent',
              color: reply.user_vote === 1 ? 'white' : '#888',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            <ArrowUp size={isMobile ? 10 : 12} />
          </button>
          
          <span style={{
            fontSize: isMobile ? '10px' : '12px',
            fontWeight: '600',
            color: reply.vote_score > 0 ? '#00d4aa' : 
                   reply.vote_score < 0 ? '#ef4444' : '#888'
          }}>
            {reply.vote_score || 0}
          </span>
          
          <button
            onClick={() => handleVoteReply(reply.user_vote === -1 ? 0 : -1)}
            style={{
              padding: '4px',
              border: 'none',
              background: reply.user_vote === -1 ? '#ef4444' : 'transparent',
              color: reply.user_vote === -1 ? 'white' : '#888',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            <ArrowDown size={isMobile ? 10 : 12} />
          </button>
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '8px', marginBottom: '8px' }}>
            <img 
              src={reply.cover_image_url || reply.profile_image_url}
              style={{
                width: isMobile ? '20px' : '24px',
                height: isMobile ? '20px' : '24px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isMobile ? '10px' : '12px',
                fontWeight: '600',
                color: 'white'
              }}
            />
            <img 
              src={getSmokerEmoji(reply.smoker_level)}
              alt={`${reply.smoker_level} level`}
              style={{
                width: isMobile ? '30px' : '38px',
                height: isMobile ? '30px' : '38px',
                borderRadius: '50%',
                objectFit: 'cover'
              }}
            />
            <span style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '600', color: '#e5e5e5' }}>
              {reply.display_name || reply.username || 'Anonymous'}
            </span>
            <span style={{ fontSize: isMobile ? '10px' : '12px', color: '#888' }}>
              {new Date(reply.created_at).toLocaleDateString()}
            </span>
          </div>
          
          <p style={{
            color: '#b3b3b3',
            lineHeight: '1.5',
            margin: '0 0 8px 0',
            fontSize: isMobile ? '12px' : '14px'
          }}>
            {reply.content}
          </p>

          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center'
          }}>
            <button 
              onClick={() => setShowNestedReplyForm(!showNestedReplyForm)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#888',
                cursor: 'pointer',
                fontSize: isMobile ? '11px' : '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <MessageCircle size={isMobile ? 10 : 12} />
              Reply
            </button>
            
            {nestedReplies.length > 0 && (
              <button
                onClick={() => setShowNestedReplies(!showNestedReplies)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#00d4aa',
                  cursor: 'pointer',
                  fontSize: isMobile ? '11px' : '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                {nestedReplies.length} replies
                {showNestedReplies ? <ChevronUp size={isMobile ? 10 : 12} /> : <ChevronDown size={isMobile ? 10 : 12} />}
              </button>
            )}
          </div>

          {showNestedReplyForm && (
            <form onSubmit={handleNestedReplySubmit} style={{
              marginTop: '12px',
              padding: isMobile ? '10px' : '12px',
              background: colors.bgTertiary,
              borderRadius: '8px',
              border: '1px solid #333'
            }}>
              <textarea
                value={nestedReplyContent}
                onChange={(e) => setNestedReplyContent(e.target.value)}
                placeholder={`Reply to ${reply.username}...`}
                rows={isMobile ? 2 : 2}
                style={{
                  width: '100%',
                  padding: isMobile ? '6px' : '8px',
                  backgroundColor: '#2a2a2a',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: '#e5e5e5',
                  fontSize: isMobile ? '11px' : '12px',
                  resize: 'vertical',
                  marginBottom: '8px',
                  boxSizing: 'border-box',
                  outline: 'none',
                  fontFamily: 'inherit'
                }}
              />
              <div style={{ 
                display: 'flex', 
                gap: '6px', 
                justifyContent: 'flex-end',
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                <button
                  type="button"
                  onClick={() => setShowNestedReplyForm(false)}
                  style={{
                    padding: isMobile ? '6px 12px' : '6px 12px',
                    background: 'transparent',
                    color: '#888',
                    border: '1px solid #333',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: isMobile ? '10px' : '11px',
                    order: isMobile ? 2 : 1
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!nestedReplyContent.trim() || isSubmittingNestedReply}
                  style={{
                    padding: isMobile ? '6px 12px' : '6px 12px',
                    background: nestedReplyContent.trim() && !isSubmittingNestedReply
                      ? 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)'
                      : '#333',
                    color: nestedReplyContent.trim() && !isSubmittingNestedReply ? '#0a0a0a' : '#666',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: nestedReplyContent.trim() && !isSubmittingNestedReply ? 'pointer' : 'not-allowed',
                    fontSize: isMobile ? '10px' : '11px',
                    fontWeight: '600',
                    order: isMobile ? 1 : 2
                  }}
                >
                  {isSubmittingNestedReply ? 'Posting...' : 'Reply'}
                </button>
              </div>
            </form>
          )}

          {showNestedReplies && nestedReplies && (
            <div style={{
              marginTop: '12px',
              paddingLeft: isMobile ? '8px' : '16px',
              borderLeft: '1px solid #333'
            }}>
              {nestedReplies.map((nestedReply) => (
                <NestedReplyCard 
                  key={nestedReply.id} 
                  nestedReply={nestedReply}
                  onVoteNestedReply={handleVoteNestedReply}
                  onNestedReplySubmit={handleNestedReplyToNestedReply}
                  parentReplyId={reply.id}
                  depth={0}
                  colors={colors}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export function ProductPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewStats, setReviewStats] = useState(null);
  const [reviewStatsSmoker, setReviewStatsSmoker] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const { user } = useUser();

  const { colors } = useTheme();
  const [showDispensariesModal, setShowDispensariesModal] = useState(false);

  const [reviewsError, setReviewsError] = useState(null);
  const [reviewFilter, setReviewFilter] = useState('all');
  const [expandedSections, setExpandedSections] = useState({
    terpenes: true,
    specs: true
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Add resize listener
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch product data on component mount
  useEffect(() => {
    loadProductData();
  }, [slug]);

  const loadProductData = async () => {
    try {
      setLoading(true);
      
      // Try to fetch product directly by slug
      try {
        const response = await fetch(`${API_URL}/api/products/${slug}`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const foundProduct = await response.json();
          // Transform the product data
          const transformedProduct = {
            id: foundProduct.id,
            name: foundProduct.name,
            brand: foundProduct.brand,
            category: mapProductType(foundProduct.type),
            thc: extractTHC(foundProduct.attributes),
            cbd: extractCBD(foundProduct.attributes),
            price: generatePrice(foundProduct),
            rating: foundProduct.avg_rating || foundProduct.averageRating || 0,
            reviewCount: foundProduct.review_count || foundProduct.reviewCount || 0,
            image: (foundProduct.cover_image || (foundProduct.coverImage && foundProduct.coverImage.imageUrl)) || getProductImage(foundProduct.type),
            description: foundProduct.description || `A premium ${foundProduct.type} known for its exceptional quality and effects.`,
            effects: getEffects(foundProduct.attributes),
            terpenes: getTerpenes(foundProduct.attributes),
            trending: (foundProduct.review_count || foundProduct.reviewCount || 0) > 10,
            hot: (foundProduct.avg_rating || foundProduct.averageRating || 0) > 8.5,
            wishlistCount: Math.floor(Math.random() * 100),
            viewCount: Math.floor(Math.random() * 1000) + 500
          };
          setProduct(transformedProduct);
          await loadReviews(foundProduct.id);
          return;
        }
      } catch (directFetchError) {
        console.log('Direct fetch failed, trying pagination approach...');
      }
      
      // Fallback: Fetch all products to find the one matching the slug
      const productsResponse = await ApiService.getAllProductsPagination(1, 100);
      const foundProduct = productsResponse.products.find(p => 
        p.name.toLowerCase().replace(/\s+/g, '-') === slug
      );

      if (!foundProduct) {
        setError('Product not found');
        return;
      }

      // Transform the product data
      const transformedProduct = {
        id: foundProduct.id,
        name: foundProduct.name,
        brand: foundProduct.brand,
        category: mapProductType(foundProduct.type),
        thc: extractTHC(foundProduct.attributes),
        cbd: extractCBD(foundProduct.attributes),
        price: generatePrice(foundProduct),
        rating: foundProduct.avg_rating ? parseFloat(foundProduct.avg_rating.toFixed(1)) : 0,
        reviewCount: foundProduct.review_count || 0,
        image: (foundProduct.cover_image || (foundProduct.coverImage && foundProduct.coverImage.imageUrl)) || getProductImage(foundProduct.type),
        description: foundProduct.description || `A premium ${foundProduct.type} known for its exceptional quality and effects.`,
        effects: getEffects(foundProduct.attributes),
        terpenes: getTerpenes(foundProduct.attributes),
        trending: (foundProduct.review_count || 0) > 10,
        hot: (foundProduct.avg_rating || 0) > 8.5,
        wishlistCount: Math.floor(Math.random() * 100),
        viewCount: Math.floor(Math.random() * 1000) + 500
      };

      setProduct(transformedProduct);

      // Fetch reviews for this product
      await loadReviews(foundProduct.id);

    } catch (err) {
      console.error('Error loading product:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadReviews = async (productId) => {
    try {
      const response = await fetch(`${API_URL}/api/products/${productId}/reviews`, {
        credentials: 'include'
      });

      if (response.status === 405) {
        setIsAuthenticated(false);
        setReviewsError('auth');
        return;
      }

      if (response.ok) {
        const responseData = await response.json();
        
        if (responseData.reviews) {
          setReviews(responseData.reviews);
          setReviewStats(responseData.statistics); // Store statistics
          setReviewStatsSmoker(responseData.all_reviews); // Store statistics
          
          // Update product with valid review statistics
          setProduct(prev => ({
            ...prev,
            reviewCount: responseData.statistics.valid_reviews.count,
            rating: responseData.statistics.valid_reviews.average_rating
          }));
        } else {
          setReviews(responseData);
        }
        
        setIsAuthenticated(true);
      }
    } catch (err) {
      console.error('Error loading reviews:', err);
      setReviewsError('general');
    }
  };

  let url;
  if (!isAuthenticated){
    url = getSmokerEmoji('stoner');
  }else{
    url = getSmokerEmoji(user?.smoker_level || 'stoner');
  }

  const handleSubmitReview = async (reviewData) => {
    try {
      const response = await fetch(`${API_URL}/api/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(reviewData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit review');
      }

      const newReview = await response.json();
      
      // Add the new review to the list
      setReviews(prev => [newReview, ...prev]);
      
      // Update product review count and rating
      setProduct(prev => ({
        ...prev,
        reviewCount: prev.reviewCount + 1,
        rating: ((prev.rating * prev.reviewCount) + reviewData.rating) / (prev.reviewCount + 1)
      }));

      setShowReviewForm(false);
      alert('Review submitted successfully!');
    } catch (error) {
      throw error; // Let the form handle the error display
    }
  };

  // Helper functions
  const extractTHC = (attributes) => {
    try {
      const attrs = typeof attributes === 'string' ? JSON.parse(attributes) : attributes;
      return parseFloat((attrs.THC || '0').replace('%', '')) || 0;
    } catch { return 0; }
  };

  const extractCBD = (attributes) => {
    try {
      const attrs = typeof attributes === 'string' ? JSON.parse(attributes) : attributes;
      
      if (attrs && attrs.CBD !== undefined && attrs.CBD !== null && attrs.CBD !== '') {
        const cbdValue = parseFloat(attrs.CBD.toString().replace('%', ''));
        return !isNaN(cbdValue) ? cbdValue : 'Unknown';
      }
      
      return 'Unknown';
    } catch {
      return 'Unknown';
    }
  };

  const mapProductType = (type) => {
    const map = { 'flower': 'Flower', 'cart': 'Vapes', 'edible': 'Edibles', 'pre-roll': 'Pre-rolls', 'concentrate': 'Concentrates' };
    return map[type] || 'Other';
  };

  const generatePrice = (foundProduct) => {
    console.log('HJEJIEBIJEBH')
    console.log('HJEJIEBIJEBH',foundProduct.pricing.has_pricing)
    console.log('HJEJIEBIJEBH',foundProduct.pricing.average_price)
    console.log('HJEJIEBIJEBH',foundProduct.pricing)
    if (foundProduct.pricing.has_pricing && foundProduct.pricing.average_price) {
      return `$${foundProduct.pricing.average_price}`;
    }
    
    // Fallback to mock prices
    const basePrices = { 'flower': 35, 'cart': 65, 'edible': 25, 'pre-roll': 15 };
    return `$${basePrices[foundProduct.type] || 30}`;
  };

  const getEffects = (attributes) => {
    try {
      const attrs = typeof attributes === 'string' ? JSON.parse(attributes) : attributes;
      const effectsMap = {
        'Sativa': ['Energizing', 'Creative', 'Uplifting'],
        'Indica': ['Relaxing', 'Sleep', 'Pain Relief'],
        'Hybrid': ['Balanced', 'Happy', 'Euphoric']
      };
      return effectsMap[attrs.strain_type] || ['Relaxing', 'Happy'];
    } catch { return ['Relaxing', 'Happy']; }
  };

  const handleReviewVote = async (reviewId, voteValue) => {
    try {
      const result = await ApiService.voteReview(reviewId, voteValue);
      
      // Update the review in state
      setReviews(prev => prev.map(review => 
        review.id === reviewId 
          ? { 
              ...review, 
              likes: result.total_votes,
              user_vote: result.user_vote 
            }
          : review
      ));
    } catch (error) {
      console.error('Error voting on review:', error);
      throw error;
    }
  };

  const handleReplySubmit = (reviewId) => {
    // Update reply count for the review
    setReviews(prev => prev.map(review =>
      review.id === reviewId
        ? { ...review, reply_count: (review.reply_count || 0) + 1 }
        : review
    ));
  };

  const getTerpenes = (attributes) => {
    try {
      const attrs = typeof attributes === 'string' ? JSON.parse(attributes) : attributes;
      const terpeneDatabase = {
        "myrcene": { description: "Relaxing, sedating effects", defaultPercentage: 0.8 },
        "limonene": { description: "Mood elevation, stress relief", defaultPercentage: 0.6 },
        "caryophyllene": { description: "Anti-inflammatory properties", defaultPercentage: 0.4 },
        "pinene": { description: "Alertness, memory retention", defaultPercentage: 0.5 },
        "terpinolene": { description: "Uplifting, energizing effects", defaultPercentage: 0.3 },
        "linalool": { description: "Calming, anti-anxiety", defaultPercentage: 0.4 },
        "humulene": { description: "Anti-inflammatory, appetite suppressant", defaultPercentage: 0.3 },
        "ocimene": { description: "Uplifting, decongestant", defaultPercentage: 0.2 },
        "terpineol": { description: "Relaxing, antibiotic properties", defaultPercentage: 0.2 },
        "valencene": { description: "Anti-inflammatory, insect repellent", defaultPercentage: 0.1 }
      };

      if (attrs && attrs.terpenes && Array.isArray(attrs.terpenes) && attrs.terpenes.length > 0) {
        return attrs.terpenes.map((terpName, index) => {
          const normalizedName = terpName.toLowerCase().trim();
          const terpData = terpeneDatabase[normalizedName];
          
          return {
            name: terpName.charAt(0).toUpperCase() + terpName.slice(1).toLowerCase(),
            percentage: terpData ? terpData.defaultPercentage : (0.8 - (index * 0.2)),
            description: terpData ? terpData.description : "Cannabis terpene with unique properties"
          };
        });
      }
      
      return [
        { name: "Myrcene", percentage: 0.8, description: "Relaxing, sedating effects" },
        { name: "Limonene", percentage: 0.6, description: "Mood elevation, stress relief" },
        { name: "Caryophyllene", percentage: 0.4, description: "Anti-inflammatory properties" }
      ];
      
    } catch (error) {
      console.error('Error processing terpenes:', error);
      return [
        { name: "Myrcene", percentage: 0.8, description: "Relaxing, sedating effects" },
        { name: "Limonene", percentage: 0.6, description: "Mood elevation, stress relief" },
        { name: "Caryophyllene", percentage: 0.4, description: "Anti-inflammatory properties" }
      ];
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const filteredReviews = reviews.filter(review => {
    if (reviewFilter === 'all') return true;
    if (reviewFilter === 'verified') return review.verified;
    if (reviewFilter === 'high') return review.rating >= 4;
    return true;
  });

  if (!isAuthenticated) {
    return (
      <AuthGate 
        pageName="Discover" 
        pageIcon={<Heart size={64} />}
        customMessage="Discover your perfect cannabis products with our smart recommendation engine"
        features={[
          { icon: <Heart size={20} />, text: "Swipe and rate thousands of products", color: "#ef4444" },
          { icon: <Star size={20} />, text: "Get personalized recommendations", color: "#fbbf24" },
          { icon: <TrendingUp size={20} />, text: "Build your taste profile", color: "#00d4aa" },
          { icon: <Award size={20} />, text: "Unlock achievements and rewards", color: "#8b5cf6" }
        ]}
      />
    );
  }

  // Loading state
  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        backgroundColor: '#0a0a0a', 
        color: 'white', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '3px solid #333',
            borderTop: '3px solid #00d4aa',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          Loading product...
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        backgroundColor: '#0a0a0a', 
        color: 'white', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ color: '#ef4444', marginBottom: '16px' }}>Product Not Found</h2>
          <p style={{ color: '#888' }}>{error}</p>
          <button 
            onClick={() => window.history.back()}
            style={{
              marginTop: '16px',
              padding: '12px 24px',
              background: '#00d4aa',
              color: '#0a0a0a',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Product not found
  if (!product) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        backgroundColor: '#0a0a0a', 
        color: 'white', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        Product not found
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0a0a0a',
      color: 'white',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {/* Add spin animation for loading states */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: isMobile ? '16px' : '32px 24px' 
      }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr', 
          gap: isMobile ? '24px' : '48px' 
        }}>
          
          {/* Left Column - Mobile: Full width stacked */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '24px',
            order: isMobile ? 2 : 1
          }}>
            {/* Product Image */}
            <div style={{
              background: colors.bgTertiary,
              border: '1px solid #333',
              borderRadius: '20px',
              overflow: 'hidden',
              position: 'relative'
            }}>
              {/* Status Badges */}
              <div style={{ 
                position: 'absolute', 
                top: '16px', 
                left: '16px', 
                zIndex: 2, 
                display: 'flex', 
                flexDirection: isMobile ? 'row' : 'column', 
                gap: '8px' 
              }}>
                {product.trending && (
                  <div style={{
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    color: 'white',
                    padding: isMobile ? '4px 8px' : '6px 12px',
                    borderRadius: '12px',
                    fontSize: isMobile ? '10px' : '12px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <TrendingUp size={isMobile ? 10 : 12} />
                    TRENDING
                  </div>
                )}
                {product.hot && (
                  <div style={{
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    color: 'white',
                    padding: isMobile ? '4px 8px' : '6px 12px',
                    borderRadius: '12px',
                    fontSize: isMobile ? '10px' : '12px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <Flame size={isMobile ? 10 : 12} />
                    HOT
                  </div>
                )}
              </div>

              
              {!isMobile && (
                <div style={{ position: 'relative', aspectRatio: '1' }}>
                  <img
                    src={product.image}
                    alt={product.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </div>
              )}
            </div>

            {/* Price Tracker - Hidden on mobile or simplified */}
            {!isMobile && <PriceTracker product={product} />}

            {/* Terpene Profile */}
            <div style={{
              background: colors.bgTertiary,
              border: '1px solid #333',
              borderRadius: '16px',
              overflow: 'hidden'
            }}>
              <button
                onClick={() => toggleSection('terpenes')}
                style={{
                  width: '100%',
                  padding: isMobile ? '16px' : '20px',
                  background: 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%)',
                  border: 'none',
                  borderBottom: `1px solid ${colors.border}`,
                  color: '#e5e5e5',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <h3 style={{
                  fontWeight: '600',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: isMobile ? '14px' : '16px'
                }}>
                  🌱 Terpene Profile
                </h3>
                {expandedSections.terpenes ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              
              {expandedSections.terpenes && (
                <div style={{ padding: isMobile ? '16px' : '20px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {product.terpenes.map((terpene) => (
                      <div key={terpene.name} style={{
                        padding: isMobile ? '10px' : '12px',
                        background: colors.bgCard,
                        borderRadius: '8px',
                        border: '1px solid #333'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center', 
                          marginBottom: '4px' 
                        }}>
                          <span style={{ 
                            fontWeight: '600', 
                            color: '#e5e5e5',
                            fontSize: isMobile ? '13px' : '14px'
                          }}>
                            {terpene.name}
                          </span>
                        </div>
                        <p style={{ 
                          margin: 0, 
                          fontSize: isMobile ? '11px' : '12px', 
                          color: '#888' 
                        }}>
                          {terpene.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: isMobile ? '24px' : '32px',
            order: isMobile ? 1 : 2
          }}>
            
            {/* Product Header */}
            <div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px', 
                marginBottom: '8px',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'flex-start' : 'center'
              }}>
                <span style={{ 
                  fontSize: isMobile ? '13px' : '14px', 
                  color: '#888', 
                  fontWeight: '500' 
                }}>
                  {product.brand}
                </span>
              </div>
              
              <h1 style={{
                fontSize: isMobile ? '24px' : '36px',
                fontWeight: '800',
                margin: '0 0 16px 0',
                color: '#e5e5e5',
                lineHeight: '1.2'
              }}>
                {product.name}
              </h1>

              {/* Mobile Product Image */}
              {isMobile && (
                <div style={{
                  background: colors.bgTertiary,
                  border: '1px solid #333',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  position: 'relative',
                  marginBottom: '20px'
                }}>
                  {/* Status Badges */}
                  <div style={{ 
                    position: 'absolute', 
                    top: '12px', 
                    left: '12px', 
                    zIndex: 2, 
                    display: 'flex', 
                    flexDirection: 'row', 
                    gap: '6px' 
                  }}>
                    {product.trending && (
                      <div style={{
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '8px',
                        fontSize: '10px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <TrendingUp size={10} />
                        TRENDING
                      </div>
                    )}
                    {product.hot && (
                      <div style={{
                        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '8px',
                        fontSize: '10px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <Flame size={10} />
                        HOT
                      </div>
                    )}
                  </div>

                  <div style={{ position: 'relative', aspectRatio: '16/9' }}>
                    <img
                      src={product.image}
                      alt={product.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </div>
                </div>
              )}

              <p style={{
                fontSize: isMobile ? '14px' : '16px',
                color: '#b3b3b3',
                lineHeight: '1.6',
                marginBottom: '24px'
              }}>
                {product.description}
              </p>

              {/* Rating & Stats */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: isMobile ? '16px' : '24px', 
                marginBottom: '24px',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'flex-start' : 'center'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Star size={isMobile ? 20 : 24} style={{ color: '#fbbf24', fill: '#fbbf24' }} />
                  <span style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: '700', color: '#e5e5e5' }}>
                    {reviewStats?.total_average_rating > 0 ? reviewStats.total_average_rating.toFixed(1) : product.rating.toFixed(1)}
                  </span>
                  <span style={{ fontSize: isMobile ? '14px' : '16px', color: '#888' }}>/ 5</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#888' }}>
                  <MessageCircle size={isMobile ? 14 : 16} />
                  <span style={{ fontSize: isMobile ? '13px' : '14px' }}>{reviews.length} Reviews</span>
                </div>
              </div>

              {/* High THC Warning */}
              {product.thc > 25 && (
                <div style={{
                  border: '1px solid rgba(245, 158, 11, 0.5)',
                  background: 'rgba(245, 158, 11, 0.1)',
                  color: '#f59e0b',
                  padding: isMobile ? '12px' : '16px',
                  borderRadius: '12px',
                  marginBottom: '24px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    fontWeight: '600', 
                    marginBottom: '8px',
                    fontSize: isMobile ? '13px' : '14px'
                  }}>
                    <Shield size={isMobile ? 14 : 16} />
                    Responsible Use
                  </div>
                  <p style={{ 
                    margin: 0, 
                    fontSize: isMobile ? '12px' : '14px',
                    lineHeight: '1.4'
                  }}>
                    This is a high-THC product ({product.thc}%). If you're new to cannabis, remember to start low and go slow.
                  </p>
                </div>
              )}

              {/* Product Specs */}
              <div style={{
                background: colors.bgTertiary,
                border: '1px solid #333',
                borderRadius: '16px',
                padding: isMobile ? '16px' : '20px',
                marginBottom: isMobile ? '24px' : '32px'
              }}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
                  gap: isMobile ? '12px' : '16px' 
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#888', fontSize: isMobile ? '13px' : '14px' }}>Category</span>
                    <strong style={{ color: '#e5e5e5', fontSize: isMobile ? '13px' : '14px' }}>{product.category}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#888', fontSize: isMobile ? '13px' : '14px' }}>THC</span>
                    <strong style={{ color: '#00d4aa', fontSize: isMobile ? '13px' : '14px' }}>{product.thc}%</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#888', fontSize: isMobile ? '13px' : '14px' }}>CBD</span>
                    <strong style={{ color: '#e5e5e5', fontSize: isMobile ? '13px' : '14px' }}>
                      {product.cbd === 'Unknown' ? 'Unknown' : `${product.cbd}%`}
                    </strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#888', fontSize: isMobile ? '13px' : '14px' }}>Price</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <strong style={{ 
                        color: '#00d4aa', 
                        fontSize: isMobile ? '16px' : '18px',
                        fontWeight: '700'
                      }}>
                        {product.price}
                      </strong>
                      {product?.pricing?.has_pricing ? (
                        <span style={{
                          fontSize: '10px',
                          padding: '2px 6px',
                          background: 'rgba(0, 212, 170, 0.2)',
                          color: '#00d4aa',
                          borderRadius: '4px',
                          fontWeight: '600'
                        }}>
                          AVG
                        </span>
                      ) : (
                        <span style={{
                          fontSize: '10px',
                          padding: '2px 6px',
                          background: 'rgba(139, 92, 246, 0.2)',
                          color: '#8b5cf6',
                          borderRadius: '4px',
                          fontWeight: '600'
                        }}>
                          AVG
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Effects */}
                <div style={{ 
                  marginTop: '16px', 
                  paddingTop: '16px', 
                  borderTop: '1px solid #333' 
                }}>
                  <span style={{ 
                    color: '#888', 
                    marginBottom: '8px', 
                    display: 'block',
                    fontSize: isMobile ? '13px' : '14px'
                  }}>
                    Effects
                  </span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {product.effects.map((effect, index) => (
                      <span key={effect} style={{
                        padding: isMobile ? '3px 8px' : '4px 12px',
                        backgroundColor: index % 3 === 0 ? 'rgba(0, 212, 170, 0.2)' : 
                                        index % 3 === 1 ? 'rgba(139, 92, 246, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                        color: index % 3 === 0 ? '#00d4aa' : 
                               index % 3 === 1 ? '#8b5cf6' : '#f59e0b',
                        borderRadius: '8px',
                        fontSize: isMobile ? '11px' : '12px',
                        fontWeight: '500'
                      }}>
                        {effect}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ 
                display: 'flex', 
                gap: '12px', 
                marginBottom: isMobile ? '24px' : '32px',
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                <button 
                  onClick={() => setShowDispensariesModal(true)}
                  style={{
                    flex: 1,
                    padding: isMobile ? '14px 20px' : '16px 24px',
                    background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                    color: '#0a0a0a',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: isMobile ? '14px' : '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 212, 170, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <MapPin size={isMobile ? 14 : 16} />
                  Find at Dispensary
                </button>
                
                <button style={{
                  padding: isMobile ? '14px' : '16px',
                  background: colors.bgCard,
                  color: '#e5e5e5',
                  border: '1px solid #333',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  width: isMobile ? '100%' : 'auto'
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#00d4aa'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#333'}
                >
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    gap: '8px' 
                  }}>
                    <Share2 size={isMobile ? 14 : 16} />
                    {isMobile && <span style={{ fontSize: '14px', fontWeight: '600' }}>Share</span>}
                  </div>
                </button>
              </div>
            </div>

            {/* Reviews Section */}
            <div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                marginBottom: '24px',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '16px' : '0',
                alignItems: isMobile ? 'flex-start' : 'center'
              }}>
                <h2 style={{
                  fontSize: isMobile ? '20px' : '24px',
                  fontWeight: '700',
                  color: '#e5e5e5',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Star size={isMobile ? 20 : 24} style={{ color: '#fbbf24' }} />
                  Community Reviews ({reviews.length})
                </h2>
                
                {isAuthenticated ? (
                  <button
                    onClick={() => setShowReviewForm(true)}
                    style={{
                      padding: isMobile ? '10px 16px' : '12px 20px',
                      background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                      color: '#0a0a0a',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: isMobile ? '13px' : '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      width: isMobile ? '100%' : 'auto',
                      justifyContent: 'center'
                    }}
                  >
                    <Plus size={isMobile ? 14 : 16} />
                    Write Review
                  </button>
                ) : (
                  <button
                    disabled
                    style={{
                      padding: isMobile ? '10px 16px' : '12px 20px',
                      background: '#333',
                      color: '#666',
                      border: '1px solid #333',
                      borderRadius: '12px',
                      fontSize: isMobile ? '13px' : '14px',
                      fontWeight: '600',
                      cursor: 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      width: isMobile ? '100%' : 'auto',
                      justifyContent: 'center'
                    }}
                  >
                    <Lock size={isMobile ? 14 : 16} />
                    Login to Review
                  </button>
                )}
              </div>

              {/* Review Filters */}
              <div style={{ 
                display: 'flex', 
                gap: '8px', 
                marginBottom: '24px', 
                flexWrap: 'wrap',
                justifyContent: isMobile ? 'center' : 'flex-start'
              }}>
                {[
                  { key: 'all', label: '🌟 All Reviews' },
                  { key: 'verified', label: '✅ Verified Only' },
                  { key: 'high', label: '⭐ High Rated (4+)' }
                ].map((filter) => (
                  <button
                    key={filter.key}
                    onClick={() => setReviewFilter(filter.key)}
                    style={{
                      padding: isMobile ? '6px 12px' : '8px 16px',
                      background: reviewFilter === filter.key 
                        ? 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)' 
                        : '#2a2a2a',
                      color: reviewFilter === filter.key ? '#0a0a0a' : '#e5e5e5',
                      border: '1px solid',
                      borderColor: reviewFilter === filter.key ? '#00d4aa' : '#333',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: isMobile ? '11px' : '12px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>

              {reviewStats && <ReviewStatsCard stats={reviewStats} reviewStatsSmoker={reviewStatsSmoker} colors={colors}/>}

              {/* Reviews List */}
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {reviewsError === 'auth' ? (
                  <div style={{
                    padding: isMobile ? '32px 16px' : '48px 24px',
                    textAlign: 'center',
                    background: colors.bgTertiary,
                    border: '1px solid #333',
                    borderRadius: '16px'
                  }}>
                    <Lock size={isMobile ? 32 : 48} style={{ color: '#333', marginBottom: '16px' }} />
                    <h3 style={{ 
                      color: '#e5e5e5', 
                      marginBottom: '8px',
                      fontSize: isMobile ? '16px' : '18px'
                    }}>
                      Login to View Reviews
                    </h3>
                    <p style={{ 
                      color: '#888', 
                      marginBottom: '24px',
                      fontSize: isMobile ? '13px' : '14px'
                    }}>
                      Sign in to read community reviews and share your own experience with this product.
                    </p>
                    <button
                      onClick={() => window.location.href = '/login'}
                      style={{
                        padding: isMobile ? '10px 20px' : '12px 24px',
                        background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                        color: '#0a0a0a',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: isMobile ? '13px' : '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        margin: '0 auto'
                      }}
                    >
                      <UserPlus size={isMobile ? 14 : 16} />
                      Sign In
                    </button>
                  </div>
                ) : filteredReviews.length > 0 ? (
                  filteredReviews.map((review) => (
                    <ReviewCard
                      key={review.id}
                      review={review}
                      onUpvote={handleReviewVote}
                      onDownvote={handleReviewVote}
                      onReplySubmit={handleReplySubmit}
                      colors={colors}
                    />
                  ))
                ) : (
                  <div style={{
                    padding: isMobile ? '32px 16px' : '48px 24px',
                    textAlign: 'center',
                    background: colors.bgTertiary,
                    border: '1px solid #333',
                    borderRadius: '16px'
                  }}>
                    <Star size={isMobile ? 32 : 48} style={{ color: '#333', marginBottom: '16px' }} />
                    <h3 style={{ 
                      color: '#e5e5e5', 
                      marginBottom: '8px',
                      fontSize: isMobile ? '16px' : '18px'
                    }}>
                      No reviews yet
                    </h3>
                    <p style={{ 
                      color: '#888', 
                      marginBottom: '24px',
                      fontSize: isMobile ? '13px' : '14px'
                    }}>
                      Be the first to share your experience with this product!
                    </p>
                    {isAuthenticated && (
                      <button
                        onClick={() => setShowReviewForm(true)}
                        style={{
                          padding: isMobile ? '10px 20px' : '12px 24px',
                          background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                          color: '#0a0a0a',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: isMobile ? '13px' : '14px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Write First Review
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Review Form Modal */}
      {showReviewForm && (
        <ReviewForm
          onSubmit={handleSubmitReview}
          onClose={() => setShowReviewForm(false)}
          productId={product.id}
          colors={colors}
        />
      )}
      {showDispensariesModal && (
        <ProductDispensariesModal
          isOpen={showDispensariesModal}
          onClose={() => setShowDispensariesModal(false)}
          product={product}
          dispensaryIconUrl={url} // Use the same icon URL from FindNearbyModal
        />
      )}
    </div>
  );
}